import React, { useState } from "react";
import {
  Box,
  Typography,
  Stack,
  IconButton,
  TextField,
  Paper,
  Divider,
  Chip,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutlineOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineOutlined";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import { formatCurrency } from "../../utils/currencyUtils";
import {
  addReceiptItem,
  updateCartItemFields,
  removeReceiptItem,
  resetReceipt,
  changeSaleType,
} from "../../redux/actions/receiptActions";
import ConfirmDialog from "../common/ConfirmDialog";
import AppButton from "../buttons/AppButton";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { ItemResponseDto } from "../../shared/dtos/item/ItemResponseDto";
import { SaleType } from "../../shared/enums/SaleType";

interface PosMobileViewProps {
  items: ItemResponseDto[];
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  onOpenPreview: () => void;
}

const PosMobileView: React.FC<PosMobileViewProps> = ({
  items,
  searchTerm,
  setSearchTerm,
  onOpenPreview,
}) => {
  const dispatch = useAppDispatch();
  const {
    cartItems,
    cartSubtotal: subtotal,
    saleType,
  } = useAppSelector((state) => state.receipt);
  const [isClearDialogOpen, setIsClearDialogOpen] = useState<boolean>(false);

  const handleUpdateField = (
    itemId: string,
    field: string,
    value: string | number,
  ): void => {
    if (value === "" || Number(value) >= 0) {
      dispatch(updateCartItemFields(itemId, { [field]: value }));
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 64px)",
        overflow: "hidden",
      }}
    >
      {/* SALE TYPE */}
      <Box
        sx={{
          p: 1,
          bgcolor: "background.paper",
          borderBottom: "1px solid rgba(255,255,255,0.05)",
        }}
      >
        <Stack direction="row" spacing={1}>
          <AppButton
            fullWidth
            size="small"
            variant="contained"
            pill
            color={saleType === SaleType.RETAIL ? "primary" : "inherit"}
            onClick={() => dispatch(changeSaleType(SaleType.RETAIL))}
            sx={{ opacity: saleType === SaleType.RETAIL ? 1 : 0.4, py: 1 }}
          >
            Retail
          </AppButton>
          <AppButton
            fullWidth
            size="small"
            variant="contained"
            pill
            color={saleType === SaleType.WHOLESALE ? "secondary" : "inherit"}
            onClick={() => dispatch(changeSaleType(SaleType.WHOLESALE))}
            sx={{ opacity: saleType === SaleType.WHOLESALE ? 1 : 0.4, py: 1 }}
          >
            Wholesale
          </AppButton>
        </Stack>
      </Box>

      {/* CART SUMMARY */}
      <Box
        sx={{
          p: 1,
          bgcolor: "background.paper",
          flexShrink: 0,
          maxHeight: "35vh",
          overflowY: "auto",
        }}
      >
        <Stack
          direction="row"
          sx={{
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1.5,
          }}
        >
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <ShoppingBagIcon fontSize="small" color="primary" />
            <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
              Cart({cartItems.length})
            </Typography>
          </Stack>
          <Typography
            variant="subtitle1"
            color="secondary"
            sx={{ fontWeight: "900" }}
          >
            {formatCurrency(subtotal)}
          </Typography>
        </Stack>

        <Stack spacing={1}>
          {cartItems.map((cItem) => (
            <Paper
              key={cItem.itemId}
              elevation={0}
              sx={{
                p: 1,
                bgcolor: "rgba(255,255,255,0.03)",
                borderRadius: 2,
                border: "1px solid rgba(255,255,255,0.05)",
              }}
            >
              <Stack
                direction="row"
                sx={{ justifyContent: "space-between", mb: 1 }}
              >
                <Typography
                  variant="caption"
                  noWrap
                  sx={{ fontWeight: "bold", display: "block" }}
                >
                  {cItem.name}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ marginBottom: 0.5, display: "block" }}
                >
                  {cItem.brand} | {cItem.category}
                </Typography>
                <Typography
                  variant="caption"
                  color="secondary"
                  sx={{ fontWeight: "bold" }}
                >
                  {formatCurrency(
                    (Number(cItem.price) - (Number(cItem.discount) || 0)) *
                      Number(cItem.qty),
                  )}
                </Typography>
              </Stack>
              <Stack direction="row" sx={{ alignItems: "center", spacing: 1 }}>
                <TextField
                  label="Price"
                  size="small"
                  type="number"
                  value={cItem.price}
                  onChange={(e) =>
                    handleUpdateField(cItem.itemId, "price", e.target.value)
                  }
                  slotProps={{
                    input: { style: { padding: "4px", fontSize: "0.75rem" } },
                  }}
                  sx={{ width: 100 }}
                />
                <TextField
                  label="Disc"
                  size="small"
                  type="number"
                  placeholder="0"
                  value={cItem.discount === 0 ? "" : cItem.discount}
                  onChange={(e) =>
                    handleUpdateField(cItem.itemId, "discount", e.target.value)
                  }
                  slotProps={{
                    input: { style: { padding: "4px", fontSize: "0.75rem" } },
                  }}
                  sx={{ width: 90 }}
                />
                <Box sx={{ flex: 1 }} />
                <IconButton
                  size="small"
                  onClick={() =>
                    handleUpdateField(
                      cItem.itemId,
                      "qty",
                      Number(cItem.qty) - 1,
                    )
                  }
                >
                  <RemoveCircleOutlineIcon sx={{ fontSize: 18 }} />
                </IconButton>
                <TextField
                  size="small"
                  type="number"
                  value={cItem.qty}
                  onChange={(e) =>
                    handleUpdateField(cItem.itemId, "qty", e.target.value)
                  }
                  slotProps={{
                    input: {
                      style: {
                        padding: "4px",
                        textAlign: "center",
                        fontSize: "0.75rem",
                      },
                    },
                  }}
                  sx={{ width: 80 }}
                />
                <IconButton
                  size="small"
                  onClick={() =>
                    handleUpdateField(
                      cItem.itemId,
                      "qty",
                      Number(cItem.qty) + 1,
                    )
                  }
                >
                  <AddCircleOutlineIcon sx={{ fontSize: 18 }} />
                </IconButton>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => dispatch(removeReceiptItem(cItem.itemId))}
                >
                  <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Stack>
            </Paper>
          ))}
        </Stack>
      </Box>

      {/* SEARCH */}
      <Box
        sx={{
          p: 1.5,
          bgcolor: "background.default",
          zIndex: 10,
          boxShadow: "0px 4px 10px rgba(0,0,0,0.1)",
        }}
      >
        <TextField
          fullWidth
          size="small"
          placeholder="Search Products..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ bgcolor: "background.paper", borderRadius: 2 }}
        />
      </Box>

      {/* PRODUCTS */}
      <Box sx={{ flexGrow: 1, overflowY: "auto", px: 1.5, pb: 12 }}>
        <Stack spacing={1.5}>
          {items.map((item) => (
            <Paper
              key={item.id}
              elevation={0}
              sx={{
                p: 1.5,
                bgcolor: "background.paper",
                borderRadius: 2,
                border: "1px solid rgba(255,255,255,0.05)",
                transition: "0.2s",
                "&:active": {
                  transform: "scale(0.98)",
                  bgcolor: "action.hover",
                },
              }}
              onClick={() => dispatch(addReceiptItem(item))}
            >
              <Box>
                <Typography
                  variant="body2"
                  gutterBottom
                  sx={{ fontWeight: "bold" }}
                >
                  {item.name}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                  <Chip
                    label={item.brandName}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: "0.65rem", height: 18 }}
                  />
                </Stack>
                <Stack
                  direction="row"
                  sx={{ justifyContent: "space-between", mt: 1 }}
                >
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block" }}
                    >
                      Retail
                    </Typography>
                    <Typography
                      variant="body2"
                      color="secondary"
                      sx={{ fontWeight: "bold" }}
                    >
                      {formatCurrency(item.retailPrice)}
                    </Typography>
                  </Box>
                  <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />
                  <Box sx={{ textAlign: "right" }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block" }}
                    >
                      Wholesale
                    </Typography>
                    <Typography
                      variant="body2"
                      color="success.main"
                      sx={{ fontWeight: "bold" }}
                    >
                      {formatCurrency(item.wholesalePrice)}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </Paper>
          ))}
        </Stack>
      </Box>

      {/* FOOTER */}
      <Paper
        elevation={24}
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          p: 2,
          display: "flex",
          gap: 2,
          zIndex: 100,
          bgcolor: "background.paper",
          borderRadius: "20px 20px 0 0",
        }}
      >
        <AppButton
          variant="outlined"
          color="error"
          fullWidth
          pill
          onClick={() => setIsClearDialogOpen(true)}
        >
          Clear
        </AppButton>
        <AppButton
          variant="contained"
          color="primary"
          fullWidth
          pill
          disabled={cartItems.length === 0}
          onClick={onOpenPreview}
        >
          Preview
        </AppButton>
      </Paper>

      <ConfirmDialog
        open={isClearDialogOpen}
        onClose={() => setIsClearDialogOpen(false)}
        onConfirm={() => {
          dispatch(resetReceipt());
          setIsClearDialogOpen(false);
        }}
        title="Clear Cart"
        description="Are you sure you want to clear your cart items?"
        confirmText="Clear"
        severity="error"
      />
    </Box>
  );
};

export default PosMobileView;
